import type { GmRole } from "./capabilities.ts";

/**
 * Staff types and the badge a person wears (ADR-112 decisions 1, 2, 3.2, 3.4, 5.2, 5.5 and 6.3). Pure: the gateway
 * works the number out from the account's roles and sends only that number, and nothing here reads a clock, a
 * database or a page.
 */

/**
 * The seven staff types, in the order the default badge takes them: Administrator, Player Support, Events, Builder,
 * Designer, Guide leader, Guide.
 */
export const STAFF_TYPES = ["administrator", "support", "events", "builder", "designer", "guide_lead", "guide"] as const;

/**
 * One staff type.
 */
export type StaffType = (typeof STAFF_TYPES)[number];

const TYPE_OF_ROLE: Readonly<Record<GmRole, StaffType>> = {
  support: "support",
  moderator: "support",
  administrator: "administrator",
  event: "events",
  builder: "builder",
  designer: "designer",
  lead: "administrator",
  guide: "guide",
  guide_lead: "guide_lead",
  support_lead: "support",
  event_lead: "events",
  builder_lead: "builder",
  designer_lead: "designer",
};

const LEADER_ROLES: ReadonlySet<GmRole> = new Set<GmRole>(["lead", "support_lead", "event_lead", "builder_lead", "designer_lead"]);

/**
 * The type a role belongs to. The Designer is a type of its own with its own badge (ADR-112's amendment of
 * 2026-10-28: "designers are not builders, they are designers"). `lead` is the Administrator's leader.
 *
 * @param role A role.
 * @returns Its staff type.
 */
export function staffTypeOf(role: GmRole): StaffType {
  return TYPE_OF_ROLE[role];
}

/**
 * The badge numbers, 0 to 15, append-only (ADR-112 decision 5.2); 14 and 15 are the Designer's and its leader's
 * (ADR-112's amendment of 2026-10-28), so every value of the four bits is spent. A page draws a number it does not
 * know as nothing.
 */
export const BADGE = Object.freeze({
  none: 0,
  guide1: 1,
  guide2: 2,
  guide3: 3,
  guide4: 4,
  guideLead: 5,
  support: 6,
  supportLead: 7,
  events: 8,
  eventsLead: 9,
  builder: 10,
  builderLead: 11,
  administrator: 12,
  administratorLead: 13,
  designer: 14,
  designerLead: 15,
} as const);

/**
 * A badge number, a whole number from 0 to 15.
 */
export type Badge = number;

/**
 * The largest badge number, the four bits of the status byte that carry it.
 */
export const BADGE_MAX = 15;

/**
 * Reports whether a value is a badge number.
 *
 * @param value The candidate.
 * @returns True for a whole number from 0 to 15.
 */
export function isBadge(value: number): value is Badge {
  return Number.isInteger(value) && value >= 0 && value <= BADGE_MAX;
}

/**
 * The icon key each badge from 1 to 15 draws (ADR-112 decision 6.3), held to the icon table's rows of kind `badge`.
 */
export const STAFF_BADGE_ICONS: Readonly<Record<number, string>> = Object.freeze({
  1: "staff-guide-1",
  2: "staff-guide-2",
  3: "staff-guide-3",
  4: "staff-guide-4",
  5: "staff-guide-lead",
  6: "staff-support",
  7: "staff-support-lead",
  8: "staff-events",
  9: "staff-events-lead",
  10: "staff-builder",
  11: "staff-builder-lead",
  12: "staff-admin",
  13: "staff-admin-lead",
  14: "staff-designer",
  15: "staff-designer-lead",
});

/**
 * The days of service at which a Guide gains each chevron: one from the first day, two from 365, three from 730,
 * four from 1,095 (ADR-112 decision 3.4). Frozen.
 */
export const GUIDE_STRIPE_DAYS: readonly number[] = Object.freeze([0, 365, 730, 1095]);

/**
 * Milliseconds in a day of served time: no calendar, time zone or daylight saving enters it.
 */
export const MS_PER_DAY = 86_400_000;

/**
 * A Guide's chevrons for its days of service.
 *
 * @param days Whole days served.
 * @returns 1 to 4; 1 for a count below the second threshold, a negative one or one that is not a number.
 */
export function guideStripes(days: number): number {
  let stripes = 1;
  GUIDE_STRIPE_DAYS.forEach((threshold, index) => {
    if (days >= threshold) {
      stripes = index + 1;
    }
  });
  return stripes;
}

/**
 * One `account_roles` row as far as service is concerned, instants in epoch milliseconds.
 */
export interface RoleServiceRow {
  readonly role: GmRole;
  readonly grantedAt: number;
  readonly revokedAt: number | null;
  readonly serviceFrom: number | null;
}

/**
 * Whole days an account has served as a Guide (ADR-112 decision 3.2): over every `guide` and `guide_lead` row, live
 * or revoked, the time from its start (`serviceFrom` when set and not after `grantedAt`, else `grantedAt`) to its
 * `revokedAt`, or to now for a live row. Overlapping rows are merged so no day counts twice, a gap counts for
 * nothing, and the sum is rounded down to whole days.
 *
 * @param rows The account's role rows, any roles, any order.
 * @param now The present instant, epoch milliseconds.
 * @returns Whole days, 0 for none.
 */
export function serviceDays(rows: readonly RoleServiceRow[], now: number): number {
  const spans: [number, number][] = [];
  for (const entry of rows) {
    if (entry.role !== "guide" && entry.role !== "guide_lead") {
      continue;
    }
    const start = entry.serviceFrom !== null && entry.serviceFrom <= entry.grantedAt ? entry.serviceFrom : entry.grantedAt;
    const end = entry.revokedAt ?? now;
    if (end > start) {
      spans.push([start, end]);
    }
  }
  spans.sort((a, b) => a[0] - b[0]);
  let total = 0;
  let from = 0;
  let to = 0;
  let open = false;
  for (const [start, end] of spans) {
    if (open && start <= to) {
      to = Math.max(to, end);
    } else {
      if (open) {
        total += to - from;
      }
      from = start;
      to = end;
      open = true;
    }
  }
  if (open) {
    total += to - from;
  }
  return Math.floor(total / MS_PER_DAY);
}

/**
 * The badge of one type.
 *
 * @param type The type.
 * @param leader Whether a leader role of the type is held.
 * @param days A Guide's days of service.
 * @returns The badge number.
 */
function badgeOfType(type: StaffType, leader: boolean, days: number): Badge {
  switch (type) {
    case "administrator":
      return leader ? BADGE.administratorLead : BADGE.administrator;
    case "support":
      return leader ? BADGE.supportLead : BADGE.support;
    case "events":
      return leader ? BADGE.eventsLead : BADGE.events;
    case "builder":
      return leader ? BADGE.builderLead : BADGE.builder;
    case "designer":
      return leader ? BADGE.designerLead : BADGE.designer;
    case "guide_lead":
      return BADGE.guideLead;
    case "guide":
      return BADGE.guide1 + guideStripes(days) - 1;
  }
}

/**
 * The badge worn when a type is held, or 0 when no role of the type is.
 *
 * @param roles The account's live roles.
 * @param type The type.
 * @param days A Guide's days of service.
 * @returns The badge number, 0 when the type is not held.
 */
function heldBadge(roles: readonly GmRole[], type: StaffType, days: number): Badge {
  const held = roles.filter((role) => staffTypeOf(role) === type);
  if (held.length === 0) {
    return BADGE.none;
  }
  return badgeOfType(type, held.some((role) => LEADER_ROLES.has(role)), days);
}

/**
 * The badge worn with no choice made (ADR-112 decision 5.5): the type of the account's roles that comes first in
 * `STAFF_TYPES`, as its leader's badge when a leader role of it is held.
 *
 * @param roles The account's live roles.
 * @param days The account's days of Guide service.
 * @returns The badge number, 0 for an account holding no role.
 */
export function defaultBadge(roles: readonly GmRole[], days: number): Badge {
  for (const type of STAFF_TYPES) {
    const badge = heldBadge(roles, type, days);
    if (badge !== BADGE.none) {
      return badge;
    }
  }
  return BADGE.none;
}

/**
 * The input of `wornBadge`.
 */
export interface WornBadgeInput {
  readonly roles: readonly GmRole[];
  readonly days: number;
  readonly wears: StaffType | null;
  readonly hidden: boolean;
}

/**
 * The badge a person wears: 0 when hidden; the chosen type's when it is held now; otherwise the default.
 *
 * @param input The live roles, the Guide service days, the chosen type or null, and whether the badge is hidden.
 * @returns The badge number.
 */
export function wornBadge(input: WornBadgeInput): Badge {
  if (input.hidden) {
    return BADGE.none;
  }
  if (input.wears !== null) {
    const chosen = heldBadge(input.roles, input.wears, input.days);
    if (chosen !== BADGE.none) {
      return chosen;
    }
  }
  return defaultBadge(input.roles, input.days);
}
